import { Icon } from "@iconify/react";
import { type ReactNode, useEffect, useState } from "react";
import logoIconUrl from "@/assets/brand/logo-128.png";
import { BrandLogo } from "@/components/brand-logo";
import { LanguageToggle } from "@/components/language-toggle";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { NotificationCenter } from "@/features/notifications/notification-center";
import { useI18n } from "@/locales/i18n-provider";
import { CurrentUserMenu } from "@/surfaces/standalone/components/current-user-menu";
import { ThemeToggle } from "@/surfaces/standalone/components/theme-toggle";
import { TopbarDynamicIsland } from "@/surfaces/standalone/components/topbar-dynamic-island";
import VersionUpdateNotifier from "@/surfaces/standalone/components/version-update-notifier";
import { WorkbenchCommandPalette } from "@/surfaces/standalone/components/workbench-command-palette";
import { cn } from "@/utils";

export type AppShellSidebarOptions = {
  collapsed: boolean;
  onNavigate?: () => void;
  onToggle: () => void;
  toggleLabel: string;
};

type AppShellProps = {
  children: ReactNode;
  navigationTitle: string;
  renderSidebar: (options: AppShellSidebarOptions) => ReactNode;
};

const NARROW_DESKTOP_QUERY = "(min-width: 768px) and (max-width: 1023px)";
const SIDEBAR_COLLAPSED_STORAGE_KEY = "tickbird:sidebar-collapsed";

function isNarrowDesktop() {
  return typeof window !== "undefined" && window.matchMedia(NARROW_DESKTOP_QUERY).matches;
}

function readSidebarCollapsedPreference() {
  if (typeof window === "undefined") {
    return undefined;
  }

  const value = window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY);
  if (value === "true") {
    return true;
  }
  if (value === "false") {
    return false;
  }
  return undefined;
}

function getInitialSidebarCollapsed() {
  return readSidebarCollapsedPreference() ?? isNarrowDesktop();
}

export function AppShell({ children, navigationTitle, renderSidebar }: AppShellProps) {
  const { t } = useI18n();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(getInitialSidebarCollapsed);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(NARROW_DESKTOP_QUERY);
    const handleChange = (event: MediaQueryListEvent) => {
      if (readSidebarCollapsedPreference() === undefined) {
        setSidebarCollapsed(event.matches);
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const toggleSidebar = () => {
    setSidebarCollapsed((collapsed) => {
      const nextCollapsed = !collapsed;
      window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(nextCollapsed));
      return nextCollapsed;
    });
  };

  return (
    <div className="flex h-dvh w-full flex-col overflow-hidden bg-background text-foreground">
      <header className="relative flex h-14 shrink-0 items-center">
        <div
          className={cn(
            "hidden h-full shrink-0 items-center transition-[width] duration-200 ease-out md:flex",
            sidebarCollapsed ? "w-16 justify-center px-2" : "w-68 px-5",
          )}
        >
          {sidebarCollapsed ? <img alt="Tickbird" className="size-7 shrink-0 object-contain" src={logoIconUrl} /> : <BrandLogo className="h-9 w-auto" />}
        </div>

        <div className="flex min-w-0 flex-1 items-center gap-3 px-4 lg:px-6">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button aria-label={t("shell.openNavigation")} className="md:hidden" size="icon" variant="ghost">
                <Icon data-icon="inline-start" icon="tabler:menu-2" />
              </Button>
            </SheetTrigger>
            <SheetContent className="flex w-72 flex-col p-0" showCloseButton={false} side="left">
              <SheetTitle className="sr-only">{navigationTitle}</SheetTitle>
              <div className="flex h-16 shrink-0 items-center px-5">
                <BrandLogo className="h-9 w-auto" />
              </div>
              <div className="min-h-0 flex-1">
                {renderSidebar({
                  collapsed: false,
                  onNavigate: () => setMobileOpen(false),
                  onToggle: () => setMobileOpen(false),
                  toggleLabel: t("shell.closeSidebar"),
                })}
              </div>
            </SheetContent>
          </Sheet>

          <img alt="Tickbird" className="size-7 shrink-0 object-contain md:hidden" src={logoIconUrl} />

          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 hidden h-full items-start justify-center pt-2 xl:flex">
            <TopbarDynamicIsland onOpenSearch={() => setCommandPaletteOpen(true)} />
          </div>

          <div className="ml-auto flex items-center gap-1.5">
            <NotificationCenter />
            <LanguageToggle />
            <ThemeToggle />
            <CurrentUserMenu />
          </div>
        </div>
      </header>

      <div className="flex min-h-0 min-w-0 flex-1">
        <aside className={cn("hidden shrink-0 transition-[width] duration-200 ease-out md:block", sidebarCollapsed ? "w-16" : "w-68")}>
          {renderSidebar({
            collapsed: sidebarCollapsed,
            onToggle: toggleSidebar,
            toggleLabel: sidebarCollapsed ? t("shell.expandSidebar") : t("shell.collapseSidebar"),
          })}
        </aside>

        <main className="mb-3 mr-3 min-h-0 min-w-0 flex-1 overflow-hidden rounded-xl bg-card">
          <div className="h-full overflow-y-auto">{children}</div>
        </main>
      </div>

      <WorkbenchCommandPalette open={commandPaletteOpen} onOpenChange={setCommandPaletteOpen} />
      <VersionUpdateNotifier />
    </div>
  );
}
